import { useEffect, useState, useCallback } from 'react';
import { getValidRegion } from 'src/utils/region-utils';
import { useFeatureFlag } from 'src/utils/useFeatureFlag';
import { buttonClickMetricsPublisher } from 'src/utils/metricUtils';
import { isAftxDomain } from 'src/utils/aftx-utils';
import { isFcMenuDomain } from 'src/utils/fc-utils';

const STAGE: string = "Beta";
const VALID_REGIONS = ['NA', 'EU', 'FE', 'CN'];
const WIDGET_BUTTON_ID = 'transxray-widget-btn';

const removeWidgetElements = () => {
    document.getElementById('transxray-widget-root')?.remove();
};

export const TransXRayWidget = () => {
    const [region, setRegion] = useState(getValidRegion());
    const isEnabled = useFeatureFlag('txray-widget');

    const syncRegion = useCallback(() => {
        const params = new URLSearchParams(window.location.search);
        const urlRegion = params.get('region') || 'NA';
        const validated = VALID_REGIONS.includes(urlRegion) ? urlRegion : 'NA';
        setRegion(validated);
    }, []);

    useEffect(() => {
        const origPushState = window.history.pushState.bind(window.history);
        window.history.pushState = (...args: Parameters<typeof origPushState>) => {
            origPushState(...args);
            syncRegion();
        };

        window.addEventListener('popstate', syncRegion);
        return () => {
            window.history.pushState = origPushState;
            window.removeEventListener('popstate', syncRegion);
        };
    }, [syncRegion]);

    useEffect(() => {
        if (!isEnabled ||  region === 'CN' || isAftxDomain() || isFcMenuDomain()) {
            removeWidgetElements();
            return;
        }

        const domain = STAGE.toLowerCase() === 'prod'
            ? 'transxray.transportation.amazon.dev'
            : 'gamma.transxray.transportation.amazon.dev';
        const script = document.createElement('script');
        script.src = `https://${domain}/widget/loader.js`;
        script.setAttribute('data-button-margin-bottom', '70');
        script.setAttribute('data-button-margin-right', '20');
        script.setAttribute('data-region', region === 'FE' ? 'JP' : region);
        document.body.appendChild(script);

        // The widget button is injected asynchronously by the loader, so we
        // use event delegation on the document to catch clicks whenever it
        // appears rather than binding to the element directly.
        const handleWidgetClick = (event: MouseEvent) => {
            const target = event.target as Element | null;
            if (target?.closest(`#${WIDGET_BUTTON_ID}`)) {
                buttonClickMetricsPublisher.publishCounterMonitor('TransXRayWidget', 1);
            }
        };
        document.addEventListener('click', handleWidgetClick);

        return () => {
            script.parentNode?.removeChild(script);
            document.removeEventListener('click', handleWidgetClick);
            removeWidgetElements();
        };
    }, [region, isEnabled]);

    return null;
};

export default TransXRayWidget;
